import {Injectable} from '@angular/core';
import {createEffect, ofType} from '@ngneat/effects';
import {FilterOperator} from '@progress/kendo-data-query';
import {SnackBarService} from '@q9elements/ui-kit/common';
import {FilterDescriptors, filterEntities} from '@q9elements/ui-kit/components';
import {isNumber, isString, sortBy} from 'lodash';
import {filter, from, map, switchMap, tap} from 'rxjs';

import {GridViewsDaoService} from './grid-views-dao.service';
import {GridViewsDialogService} from './grid-views-dialog.service';
import {GridViewsService} from './grid-views.service';
import {DEFAULT_VIEW_ID} from '../models/constants';
import {
  createView,
  deleteView,
  editFilters,
  editView,
  loadViews,
  selectView,
  updateGridFilters
} from '../models/grid-views.actions';
import {GridView} from '../models/interfaces';

@Injectable()
export class GridViewEffects {
  loadViews$ = createEffect(actions => {
    return actions.pipe(
      ofType(loadViews),
      switchMap(() => from(this.gridViewsDaoService.fetchViews())),
      map(views => sortBy(views, ['name'])),
      // Compatibility fix for the new UI
      map(views => views.map(this.makeViewCompatibleWithNewUI)),
      tap(views => {
        this.gridViewsService.storeViews(views);

        const currentViewId = this.gridViewsService.selectedViewId();
        const isActual = views.some(view => view.id === currentViewId);

        if (currentViewId && !isActual) {
          actions.dispatch(selectView({id: DEFAULT_VIEW_ID}));
        }
      })
    );
  });

  selectView$ = createEffect(actions => {
    return actions.pipe(
      ofType(selectView),
      tap(({id}) => this.gridViewsService.selectView(id)),
      tap(() => actions.dispatch(updateGridFilters({})))
    );
  });

  createView$ = createEffect(actions => {
    return actions.pipe(
      ofType(createView),
      map(({view}) => {
        if (view) {
          return this.gridViewsService.cloneView(view);
        }

        return view;
      }),
      switchMap(view => this.gridViewsDialogService.create(view)),
      tap(view => this.gridViewsService.addView(view)),
      tap(view => actions.dispatch(selectView({id: view.id})))
    );
  });

  editView$ = createEffect(actions => {
    return actions.pipe(
      ofType(editView),
      switchMap(({view}) => this.gridViewsDialogService.edit(view)),
      tap(view => this.gridViewsService.editView(view)),
      tap(() => this.snackBarService.success('GRID_VIEWS.SUCCESSFUL_UPDATE'))
    );
  });

  deleteView$ = createEffect(actions => {
    return actions.pipe(
      ofType(deleteView),
      filter(({view}) => view.id !== this.gridViewsService.selectedViewId()),
      switchMap(({view}) => this.gridViewsDialogService.delete(view).pipe(map(() => view.id))),
      tap(id => this.gridViewsService.deleteView(id)),
      tap(() => this.snackBarService.success('GRID_VIEWS.SUCCESSFUL_DELETE'))
    );
  });

  editFilters$ = createEffect(actions => {
    return actions.pipe(
      ofType(editFilters),
      switchMap(({view}) => this.gridViewsDialogService.editFilters(view)),
      tap(({id, filters}) => this.gridViewsService.editFilters(id, filters)),
      tap(() => this.snackBarService.success('GRID_VIEWS.SUCCESSFUL_UPDATE')),
      filter(({id}) => id === this.gridViewsService.selectedViewId()),
      tap(({filters}) => actions.dispatch(updateGridFilters({filters})))
    );
  });

  updateGridFilters$ = createEffect(actions => {
    return actions.pipe(
      ofType(updateGridFilters),
      map(({filters}) => filters || this.gridViewsService.selectedView()?.filters || []),
      tap(filters => actions.dispatch(filterEntities({filters})))
    );
  });

  constructor(
    private snackBarService: SnackBarService,
    private gridViewsService: GridViewsService,
    private gridViewsDaoService: GridViewsDaoService,
    private gridViewsDialogService: GridViewsDialogService
  ) {}

  // Function to make filters compatible with the grid on new UI
  private makeViewCompatibleWithNewUI(view: GridView) {
    if (!view.filters) {
      return view;
    }

    view.filters = view.filters.reduce((acc, filter) => {
      const value = filter.value;

      if (!value || isString(value) || isNumber(value)) {
        acc.push(filter);
      } else if (!Array.isArray(value)) {
        // Object is only used for assignee filter
        // @ts-ignore
        filter.value = value.id || value._id || String(value);

        acc.push(filter);
      } else {
        // Array is only used for date range filter
        const [from, to] = value;

        if (from) {
          acc.push({
            field: filter.field,
            operator: FilterOperator.GreaterThanOrEqual,
            value: from
          });
        }

        if (to) {
          acc.push({
            field: filter.field,
            operator: FilterOperator.LessThanOrEqual,
            value: to
          });
        }
      }

      return acc;
    }, [] as FilterDescriptors);

    return view;
  }
}
